/**
 * sseReport.js — client-side jsPDF port of the Surgical Suite Engine PDF report
 * (SOURCE OF TRUTH: apps/surgical-suite-engine/sidecar/routers/export.py
 * `generate_report`, branch feat/sse-metal-cleanup). Reproduces the exact 3-page
 * layout so the design tool's output matches SSE. If the SSE ReportLab layout
 * changes, mirror it here.
 *
 * A4 portrait, margins 10/10/10/26 mm. Per-page tiled "Osteo3d ®" watermark +
 * branded footer. Page 1 = 6 ortho views (A/P/LL/RL/S/I) 3×2 grid (or, Option 2,
 * one view per page). Page 2 = Case Information (case notes + CT data + ISO +
 * quality waiver). Page 3 = Model & Process (mesh metrics + imaging + steps).
 *
 * Pages 2 and 3 are BOTH optional — `includeCaseInfo` (default on) and
 * `includeModelProcess` (default off). Page 2 used to be unconditional and titled
 * "Preview Images" on a page containing no preview images, which is why the team
 * reported unchecking the page-3 box and still getting "the page": they were
 * looking at page 2, and nothing was wired to switch it off.
 */

import {
  PW, PH, BRAND, sanitize, registerMulish, tiledWatermark, asDataUrl, toJpeg,
} from './pdfChassis.js'

/**
 * The CT rows the report can draw: [label, ctInfo key, { core?, red? }].
 *
 * `core` means "render even when empty" — a dash then says "we looked, this scan
 * does not record it", which is information when metadata was fetched and noise
 * when it was not.
 *
 * EXPORTED, and CT_FIELD_KEYS is DERIVED from it, because PreviewPage decides
 * whether to warn "no DICOM metadata found" while this file decides whether to
 * draw the block — and they used to be two hand-maintained lists. A review found
 * only a length assertion pinning them, so adding a row here would have silently
 * reintroduced the banner-lies-to-the-PDF drift this exists to prevent. One list,
 * one answer, no way to desynchronise.
 */
export const CT_FIELDS = [
  ['Scan type', 'scan_type', { core: 1 }], ['Scan region', 'scan_region', { core: 1 }],
  ['Contrast', 'contrast', { core: 1 }], ['Data type', 'data_type', { core: 1 }],
  ['Patient age', 'patient_age', { core: 1 }],
  ['Series no', 'series_no'], ['Series name', 'series_name', { core: 1 }],
  ['Series type', 'series_type', { core: 1 }],
  ['Slice thickness', 'slice_thickness', { core: 1 }], ['Slice spacing', 'slice_spacing'],
  ['Spacing source', 'spacing_source'],
  ['Convolution kernel', 'kernel'], ['Window', 'window_type'], ['Window width', 'window_width'],
  ['kVp', 'kvp'], ['Exposure (mAs)', 'mas'], ['Gantry tilt', 'gantry_tilt'],
  ['CBCT', 'cbct'], ['Compressed', 'compressed'], ['Transfer syntax', 'transfer_syntax'],
  ['Manufacturer', 'manufacturer'], ['Scanner model', 'scanner_model'],
  ['Institution', 'institution'], ['Referring physician', 'referring_physician'],
  ['Study date', 'study_date', { core: 1, red: true }], ['DICOM received', 'dicom_received', { core: 1 }],
]

export const CT_FIELD_KEYS = CT_FIELDS.map(([, key]) => key)

/**
 * Sub-heading for the CT block, from the scan's own BodyPartExamined (0018,0015).
 *
 * Was the hardcoded string "Skull data" — on O3D-2026-08-01-0004, a FOOT scan whose
 * every other field correctly read EXTREMITY. Both generators printed it, so the
 * error was consistent across the two documents and looked deliberate.
 *
 * Falls back to the neutral "Scan data" rather than guessing: an unlabelled region is
 * unknown, and inventing an anatomy on a surgical document is the defect, not the
 * blank. Mirrored in sidecar/routers/export.py `_ct_subheading`.
 */
export function ctSubheading(ctInfo = {}) {
  const region = String(ctInfo.scan_region || '').trim()
  if (!region) return 'Scan data'
  const pretty = region.length <= 3
    ? region.toUpperCase()
    : region.charAt(0).toUpperCase() + region.slice(1).toLowerCase()
  return `${pretty} data`
}

/** Does this ctInfo contain anything the report can print? */
export const hasDisplayableCtData = (ctInfo = {}) =>
  CT_FIELD_KEYS.some((k) => ctInfo[k] != null && ctInfo[k] !== '')

const M = 10
const USABLE = PW - 2 * M            // 190
const FOOTER_TOP = PH - 26           // content must stay above this

const { CYAN, DARK, RED, GREY, LGREY, INK, RULE, GRID, THEAD } = BRAND
let FONT = 'helvetica'               // → 'Mulish' once the TTF is embedded (registerMulish)

const esc = (v) => (v == null || v === '' ? '—' : sanitize(v))

// ── per-page frame: tiled watermark + branded footer (SSE _page_frame) ──────────
function stampFrame(doc, pageNo, caseId, dateStr) {
  tiledWatermark(doc, FONT)

  // footer
  doc.setFont(FONT,'normal')
  doc.setFontSize(8)
  doc.setTextColor(...GREY)
  doc.text(`${caseId || 'CASE'}_report.pdf`, M, PH - 25)
  doc.text(dateStr, PW - M, PH - 25, { align: 'right' })
  doc.setFont(FONT,'bold')
  doc.setFontSize(10)
  doc.setTextColor(0, 0, 0)
  doc.text('Images are not to scale', PW / 2, PH - 20, { align: 'center' })
  doc.setFont(FONT,'normal')
  doc.setFontSize(7)
  doc.setTextColor(...GREY)
  doc.text("Copyright © 2026 Osteo3d®. All rights reserved. 'Osteo3d®' and '3d printing healthcare®' are registered trademarks. Unauthorized use/replication is prohibited.",
    PW / 2, PH - 15, { align: 'center', maxWidth: USABLE })
  doc.text('Phone: +91-9900265669    Email: contactus@osteo3d.com', PW / 2, PH - 11.5, { align: 'center' })
  doc.text('Osteo3d® Surgical Planning Report', M, PH - 7)
  doc.text(`Page ${String(pageNo).padStart(2, '0')}`, PW - M, PH - 7, { align: 'right' })
}

// ── shared header block (logo + subtitle + 2×2 info bar) → returns yEnd ─────────
// Exported for tests: the info-bar overlap regression is measured directly.
export function headerBlock(doc, subtitle, info, logo, logoAspect) {
  let y = M
  if (logo) {
    const h = 14, w = h * (logoAspect || 3)
    try { doc.addImage(logo, 'PNG', (PW - w) / 2, y, w, h) } catch { /* ignore */ }
    y += h + 3
  } else {
    y += 4
  }
  doc.setFont(FONT,'bold')
  doc.setFontSize(13)
  doc.setTextColor(...DARK)
  doc.text(subtitle, PW / 2, y + 3, { align: 'center' })
  y += 8

  // info bar 2×2 with rules above/below.
  //
  // Values are WRAPPED inside their half-column and the row grows to fit. Each cell
  // used to be drawn with a bare doc.text() at a fixed x — no width limit, no
  // wrapping, no truncation — so a long value ran straight across the page and
  // overprinted its right-hand neighbour. Reported by the team (2026-07-27):
  // "Hospital name: Faculty of Dental Science, M S Ramaiah University of Applied
  // Sciences, Bangalore" printed through "Case ID: O3D-2026-07-27-0003".
  // A single-line row lays out exactly as before, so short values are unchanged.
  const rowH = 6
  const lineH = 4.2                 // leading for continuation lines within a cell
  const colW = USABLE / 2
  const gutter = 3                  // keeps a wrapped value clear of the next column
  doc.setDrawColor(...RULE)
  doc.setLineWidth(0.4)
  doc.line(M, y, PW - M, y)
  doc.setFontSize(10)

  const measure = (label, val) => {
    doc.setFont(FONT, 'bold')
    const lw = doc.getTextWidth(`${label}: `)
    doc.setFont(FONT, 'normal')
    const avail = Math.max(colW - lw - gutter, 12)
    const text = esc(val) || ''
    return { lw, lines: text ? doc.splitTextToSize(text, avail) : [''] }
  }
  const draw = (label, m, cx, cy) => {
    doc.setFont(FONT, 'bold'); doc.setTextColor(...INK)
    doc.text(`${label}: `, cx, cy)
    doc.setFont(FONT, 'normal')
    m.lines.forEach((ln, i) => doc.text(ln, cx + m.lw, cy + i * lineH))
  }

  const infoRows = [
    [['Surgeon name', info.surgeonName], ['Patient name', info.patientName]],
    [['Hospital name', info.hospital], ['Case ID', info.caseId]],
  ]
  let rowTop = y
  for (const [left, right] of infoRows) {
    const lm = measure(left[0], left[1])
    const rm = measure(right[0], right[1])
    const lines = Math.max(lm.lines.length, rm.lines.length, 1)
    const cy = rowTop + rowH - 1.5
    draw(left[0], lm, M, cy)
    draw(right[0], rm, M + colW, cy)
    rowTop += rowH + (lines - 1) * lineH
  }
  y = rowTop
  doc.line(M, y, PW - M, y)
  // additional note (e.g. "Mandible will be printed separately") centred under the info bar
  if (info.note) {
    doc.setFont(FONT, 'normal'); doc.setFontSize(10); doc.setTextColor(...INK)
    doc.text(esc(info.note), PW / 2, y + 5, { align: 'center' })
    return y + 13
  }
  return y + 10
}

// aspect-fit an image inside a box (mm), centered, with a light border. Embeds JPEG.
async function drawFittedImage(doc, b64, x, y, boxW, boxH, border = true) {
  const { url, ar } = await toJpeg(asDataUrl(b64))
  let w = boxW, h = boxW / ar
  if (h > boxH) { h = boxH; w = boxH * ar }
  const dx = x + (boxW - w) / 2, dy = y + (boxH - h) / 2
  if (border) { doc.setDrawColor(...LGREY); doc.setLineWidth(0.3); doc.rect(x, y, boxW, boxH) }
  try { doc.addImage(url, 'JPEG', dx, dy, w, h) } catch { /* ignore */ }
}

function heading(doc, text, y) {
  doc.setFont(FONT,'bold'); doc.setFontSize(14); doc.setTextColor(...CYAN)
  doc.text(text, M, y)
  return y + 6
}

// simple 2/3-col table
function drawTable(doc, x, y, rows, colW, { header = false } = {}) {
  const rowH = 6.5
  doc.setFontSize(9); doc.setLineWidth(0.2); doc.setDrawColor(...GRID)
  rows.forEach((row, ri) => {
    let cx = x
    const cy = y + ri * rowH
    const isHead = header && ri === 0
    if (isHead) { doc.setFillColor(...THEAD); doc.rect(x, cy, colW.reduce((a, b) => a + b, 0), rowH, 'F') }
    row.forEach((cellText, ci) => {
      doc.rect(cx, cy, colW[ci], rowH)
      doc.setFont(FONT,isHead ? 'bold' : 'normal')
      doc.setTextColor(...(isHead ? DARK : INK))
      const lines = doc.splitTextToSize(String(cellText ?? ''), colW[ci] - 3)
      doc.text(lines[0] ?? '', cx + 1.5, cy + rowH - 2)
      cx += colW[ci]
    })
  })
  return y + rows.length * rowH
}

/**
 * One line stating what the CT block rests on.
 *
 * WHY THE REPORT SAYS THIS. On O3D-2026-08-01-0004 this page named series 1011
 * "MPR sag 0.6mm Range" while the model was built from series 2 "Foot or Ankle 1.00
 * Br56" — and printed it in the same type, with the same confidence, as every value
 * that WAS correct. The operator had already noticed and retyped the real scan
 * details into Case Notes by hand. Nothing on the page distinguished the two.
 *
 * Three states, never collapsed into two:
 *   confirmed  SSE told us which series was reconstructed, and it matches
 *   MISMATCH   SSE told us, and the portal analysed a DIFFERENT series
 *   unverified nobody told us; these values are the portal's own re-derivation
 *
 * `seriesMismatch: false` alone cannot distinguish "they agree" from "we could not
 * check", which is why mergeSseMeta also returns `seriesMatchedOn`.
 *
 * @returns {{ text: string, warn: boolean } | null}
 */
export function ctProvenanceNote(ctSource) {
  if (!ctSource) return null
  if (!ctSource.applied) {
    return {
      text: 'Scan details re-derived from the DICOM archive — not confirmed against '
        + 'the series used to build the model.',
      warn: true,
    }
  }
  if (ctSource.seriesMismatch) {
    return {
      text: `Series MISMATCH: the model was built from series ${ctSource.seriesNumber ?? '?'}`
        + `${ctSource.seriesDescription ? ` (${ctSource.seriesDescription})` : ''}, but the `
        + `quality figures were measured on series ${ctSource.portalSeriesNumber ?? '?'}.`,
      warn: true,
    }
  }
  const how = ctSource.selectionMode === 'operator-override' ? 'chosen by the operator'
    : ctSource.selectionMode === 'operator-confirmed' ? 'confirmed by the operator'
      : ctSource.selectionMode === 'single-series' ? 'the only series in the study'
        : 'recorded at reconstruction'
  const checked = ctSource.seriesMatchedOn === 'uid' ? 'verified by series UID'
    : ctSource.seriesMatchedOn === 'seriesNumber' ? 'verified by series number'
      : 'not cross-checked'
  return {
    text: `Scan details from the series actually reconstructed (${how}; ${checked}).`,
    warn: false,
  }
}

/**
 * @returns {Promise<jsPDF>} the built document (caller .save()s it)
 */
export async function buildSseReport(data) {
  const { jsPDF } = await import('jspdf')
  const {
    caseId = '', patientName = '', surgeonName = '', hospital = '', date = '',
    caseNote = '', otherNotes = '', page1Title = 'Images for Confirmation of Fabrication',
    qualityWaiver = false, waiverNotes = '', note = '',
    ctInfo = {}, imaging = {}, meshMetrics = {}, processSteps = '',
    // Provenance summary from GET /render/case/:id/report-meta (`sse` in the
    // response, produced by services/sseMeta.js mergeSseMeta). Null when the case
    // was never fetched. Footnoted under the CT block — see ctProvenanceNote().
    ctSource = null,
    logo = null, logoAspect = 3, ortho = [], orthoCodes = [], iso = '',
    layout = 'grid', dateStr = '', includeModelProcess = false,
    // Page 2. Default TRUE so existing output is unchanged — but the team asked
    // for the same control they already had over page 3, having reasonably
    // assumed the one checkbox covered both metadata pages.
    includeCaseInfo = true,
  } = data

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' })
  FONT = await registerMulish(doc)
  const info = { surgeonName, patientName, hospital, caseId, note }

  // ===== PAGE 1 — the 6 ortho views =====
  if (layout === 'pages' && ortho.length) {
    // Option 2 — one view per page (design extra), same report chrome.
    const LABELS = { A: 'Anterior', P: 'Posterior', LL: 'Left Lateral', RL: 'Right Lateral', S: 'Superior', I: 'Inferior', ISO: 'Isometric' }
    for (let i = 0; i < ortho.length; i++) {
      if (i > 0) doc.addPage()
      const y = headerBlock(doc, page1Title, info, logo, logoAspect)
      await drawFittedImage(doc, ortho[i], M, y, USABLE, FOOTER_TOP - y - 12)
      doc.setFont(FONT,'bold'); doc.setFontSize(12); doc.setTextColor(...CYAN)
      doc.text(LABELS[orthoCodes[i]] || orthoCodes[i] || '', PW / 2, FOOTER_TOP - 6, { align: 'center' })
    }
  } else {
    const y = headerBlock(doc, page1Title, info, logo, logoAspect)
    // 3 rows × 2 cols, each cell 95×63 mm
    const cellW = USABLE / 2, cellH = 63
    for (let i = 0; i < 6; i++) {
      const b64 = ortho[i]
      const col = i % 2, rowi = Math.floor(i / 2)
      const cx = M + col * cellW, cy = y + rowi * cellH
      if (b64) await drawFittedImage(doc, b64, cx, cy, cellW, cellH)
      else { doc.setDrawColor(...LGREY); doc.setLineWidth(0.3); doc.rect(cx, cy, cellW, cellH) }
    }
  }

  // ===== PAGE 2 — Case Information (case notes + CT data + ISO + quality waiver) =====
  const oldScan = Number(ctInfo.study_age_days) > 30
    // The single exported list; only the red highlight is per-report.
    const ctFields = CT_FIELDS.map(([label, key, opts]) => (
      key === 'study_date' ? [label, key, { ...opts, red: oldScan }] : [label, key, opts]
    ))
  // Is there any metadata AT ALL? `core` means "render even when empty", which is
  // right when the metadata WAS fetched — "Contrast: —" then says "we looked, this
  // scan does not record it". It is meaningless when nothing was fetched, and the
  // team reported the result: ten labels against ten dashes, ~54 mm of nothing.
  //
  // ctInfo is {} in four real situations — Upload mode with the optional Case ID
  // left blank, a Case ID under 8 characters, a failed fetch, and a case with no
  // DICOM (the backend returns empty objects rather than an error). So this is the
  // normal state for an ad-hoc mesh, not an edge case.
  const hasCtData = hasDisplayableCtData(ctInfo)

  // Does page 2 carry any CASE-INFORMATION content — the four things the heading
  // "Case Information" actually promises? The waiver is deliberately NOT in this
  // list: it is not information about the case, it is a statement about the scan's
  // fitness, and it is handled separately below.
  const caseInfoContent = Boolean(caseNote || otherNotes || hasCtData || iso)
  // Waiver NOTES do keep the page alive (they are text the operator typed and must
  // not vanish), but they are not case information — see page2Title.
  const page2HasContent = caseInfoContent || Boolean(waiverNotes)

  // The body the checkbox governs…
  const showCaseInfo = includeCaseInfo && page2HasContent
  // …and the statement it does NOT get to suppress.
  //
  // A quality waiver says the operator accepted a substandard scan. On a surgical
  // fabrication document that must appear whatever else is switched off — so an
  // affirmative waiver forces the page even when the operator has unchecked
  // "Include Case Information page". Without this, a checkbox captioned "case notes
  // + CT data + isometric" silently deleted a quality statement, which is exactly
  // the thing the page2HasContent comment above swore not to do.
  const showPage2 = showCaseInfo || qualityWaiver

  // Declared OUTSIDE the page-2 block: page 3 reuses it. Scoping it inside the guard
  // makes page 3 throw `ReferenceError: y is not defined` (module code is strict) —
  // a hard crash, not a silent wrong layout.
  let y = 0
  // Title the page for what it actually carries. When only a quality waiver forced
  // it into existence, "Case Information" is the one thing it does NOT contain —
  // and a near-empty page under a wrong heading reads as a bug to the same team
  // that reported blank space.
  //
  // Keyed on caseInfoContent, NOT on showCaseInfo: waiver notes alone satisfy
  // showCaseInfo (so the typed text is not dropped) but put nothing under the
  // heading. Reachable — "Isometric" is a view checkbox, so an operator who
  // unticks it, has no DICOM and types no case note gets a page whose only content
  // is the waiver. Titling that "Case Information" is the same defect as titling
  // a page with no preview images "Preview Images".
  const page2Title = (showCaseInfo && caseInfoContent) ? 'Case Information' : 'Quality Waiver'
  if (showPage2) {
    doc.addPage()
    y = headerBlock(doc, page2Title, info, logo, logoAspect)
    if (showCaseInfo && (caseNote || otherNotes)) {
      y = heading(doc, 'Case Notes', y)
      doc.setFont(FONT,'normal'); doc.setFontSize(10); doc.setTextColor(...INK)
      if (caseNote) { const l = doc.splitTextToSize(esc(caseNote), USABLE); doc.text(l, M, y); y += l.length * 5 }
      if (otherNotes) { const l = doc.splitTextToSize(esc(otherNotes), USABLE); doc.text(l, M, y); y += l.length * 5 }
      y += 3
    }
    const colTop = y
    // right — ISO render (drawn first; the CT list flows down the LEFT strip only, so a
    // long metadata list never collides with it)
    // Remember WHICH page the ISO landed on. The waiver's wrap width depends on
    // whether the image is beside it, and after a page break it is not.
    let isoPageNo = -1
    if (showCaseInfo && iso) {
      isoPageNo = doc.internal.getCurrentPageInfo().pageNumber
      await drawFittedImage(doc, iso, M + USABLE / 2, colTop, USABLE / 2, 108, false)
    }
    // left — CT data information, omitted ENTIRELY when there is no metadata rather
    // than printed as a skeleton of dashes.
    let ly = y
    let ctOverflow = false // did the list spill onto a continuation page?
    if (showCaseInfo && hasCtData) {
    heading(doc, 'CT data information', y)
    ly = y + 6
    doc.setFont(FONT,'bold'); doc.setFontSize(10); doc.setTextColor(...INK)
    doc.text(ctSubheading(ctInfo), M, ly); ly += 6
    const labelX = M, valueX = M + 38, valueW = USABLE / 2 - 40, lineH = 5.4
    doc.setFontSize(9)
    for (const [label, key, opts = {}] of ctFields) {
      const val = ctInfo[key]
      if (!opts.core && (val == null || val === '')) continue
      const lines = doc.splitTextToSize(esc(val), valueW)
      if (ly + lines.length * lineH > FOOTER_TOP - 4) { // overflow → continuation page
        doc.addPage()
        ly = headerBlock(doc, `${page2Title} (cont.)`, info, logo, logoAspect)
        ly = heading(doc, 'CT data information (cont.)', ly) + 2
        doc.setFontSize(9); ctOverflow = true
      }
      doc.setFont(FONT, 'normal'); doc.setTextColor(...INK)
      doc.text(`${label}:`, labelX, ly)
      if (opts.red) { doc.setFont(FONT, 'bold'); doc.setTextColor(...RED) }
      doc.text(lines, valueX, ly)
      ly += Math.max(1, lines.length) * lineH
    }
    // Say what these values REST ON. Wrapped to the LEFT column only, like the list
    // above it, so it can never run under the ISO render on the right.
    const note = ctProvenanceNote(ctSource)
    if (note) {
      const noteLineH = 3.6
      ly += 2
      doc.setFontSize(8)
      doc.setFont(FONT, note.warn ? 'bold' : 'normal')
      doc.setTextColor(...(note.warn ? RED : GREY))
      let noteLines = doc.splitTextToSize(note.text, USABLE / 2 - 2)
      if (ly + noteLines.length * noteLineH > FOOTER_TOP - 4) {
        doc.addPage()
        ly = headerBlock(doc, `${page2Title} (cont.)`, info, logo, logoAspect)
        ctOverflow = true
        // Re-apply after headerBlock, which sets its own font/size/colour.
        doc.setFontSize(8)
        doc.setFont(FONT, note.warn ? 'bold' : 'normal')
        doc.setTextColor(...(note.warn ? RED : GREY))
        noteLines = doc.splitTextToSize(note.text, USABLE)
      }
      doc.text(noteLines, M, ly)
      ly += noteLines.length * noteLineH
      doc.setFontSize(9); doc.setTextColor(...INK)
    }
    }
    // Where the waiver goes.
    //
    // It is drawn at the LEFT margin; the ISO occupies the RIGHT half. The two can
    // never collide horizontally, so the only reason to drop below the ISO band is
    // when the CT list on the left has itself run down past it. Reserving 108 mm
    // unconditionally was the second and larger of the two gaps the team reported —
    // with no metadata it pushed the waiver most of a page down, past nothing.
    if (ctOverflow) y = ly + 4                                     // list spilled; no ISO on that page
    else if (showCaseInfo && hasCtData && iso) y = Math.max(ly, colTop + 108) + 4  // clear whichever column is taller
    else y = ly + 4                                                // nothing on the left to clear
    // The waiver block MEASURES what it is about to draw, then paginates.
    //
    // A first attempt budgeted a flat `6 + 12` mm for the notes. The real line
    // height is 4.06 mm (10 pt × jsPDF's 1.15 factor), so 12 mm buys 2.96 lines —
    // and `waiverNotes` is unbounded free text. At ~635 characters the notes
    // printed over the footer filename, and at ~762 straight through "Images are
    // not to scale". Fitting a constant to a variable does not guard anything.
    //
    // `y` was also advanced by a flat 6 mm regardless of how many lines were
    // drawn, so nothing downstream knew where the block actually ended.
    const lineH = doc.getLineHeight() / doc.internal.scaleFactor
    const newWaiverPage = () => {
      doc.addPage()
      // "(cont.)" so three consecutive pages titled the same do not look like a bug
      return headerBlock(doc, `${page2Title} (cont.)`, info, logo, logoAspect)
    }
    // The waiver line itself needs 6 mm; if that will not fit, move first — then
    // the notes are measured against a page that has no ISO on it.
    if (y + 6 > FOOTER_TOP - 2) y = newWaiverPage()

    doc.setFont(FONT,'bold'); doc.setFontSize(10); doc.setTextColor(...INK)
    doc.text('Quality waiver: ', M, y)
    const qw = doc.getTextWidth('Quality waiver: ')
    doc.setTextColor(...(qualityWaiver ? RED : DARK))
    doc.text(qualityWaiver ? 'Yes' : 'No', M + qw, y)
    y += 6

    if (waiverNotes) {
      doc.setFont(FONT,'normal'); doc.setFontSize(10); doc.setTextColor(...INK)
      // The notes FLOW AROUND the ISO, they are not blanket-narrowed by it.
      //
      // The previous rule asked "is the ISO on this page?" and, if so, wrapped
      // every line to half width. But the ISO occupies the right half only
      // between `colTop` and `colTop + 108`, and when the case HAS CT data the
      // waiver is already placed below that band — so the notes were squeezed
      // into a 91 mm column to clear an image that was nowhere near them,
      // leaving the right half of the page blank. Reported from the field:
      // "what is this wrapped in the middle of the page? It should be along the
      // entire page but not go beyond the margin."
      //
      // So the width is decided PER LINE, from where that line actually sits:
      // narrow while the line is level with the image, full width once it is
      // past it — and full width on any continuation page, which has no image.
      const isoBottom = isoPageNo > 0 ? colTop + 108 : -Infinity
      const words = `Notes: ${esc(waiverNotes)}`.split(/\s+/).filter(Boolean)
      let i = 0
      while (i < words.length) {
        if (y + lineH > FOOTER_TOP - 2) y = newWaiverPage()
        const besideIso = doc.internal.getCurrentPageInfo().pageNumber === isoPageNo && y <= isoBottom
        const width = besideIso ? USABLE / 2 - 4 : USABLE
        // Greedy fill, the same rule splitTextToSize uses — but re-evaluated for
        // each line, which is what a single call cannot do.
        let line = words[i]
        let j = i + 1
        if (doc.getTextWidth(line) > width) {
          // A single token wider than the column has to be BROKEN, not drawn
          // past the margin. splitTextToSize does this and the first version of
          // this loop did not, which a guard caught immediately: 6000 unbroken
          // 'W's came out as one line running off the sheet instead of
          // paginating.
          let k = 1
          while (k < line.length && doc.getTextWidth(line.slice(0, k + 1)) <= width) k += 1
          words[i] = line.slice(k)      // the rest is the next "word"
          line = line.slice(0, k)
          j = i
        } else {
          while (j < words.length && doc.getTextWidth(`${line} ${words[j]}`) <= width) {
            line += ` ${words[j]}`
            j += 1
          }
        }
        doc.text(line, M, y)
        y += lineH
        i = j
      }
    }
  }

  // ===== PAGE 3 — Model & Process =====
  const hasMetrics = meshMetrics && (meshMetrics.faces || meshMetrics.vertices)
  const hasImaging = imaging && (imaging.grade || (imaging.checks && imaging.checks.length))
  if (includeModelProcess && (hasMetrics || hasImaging || processSteps)) {
    doc.addPage()
    y = headerBlock(doc, 'Model & Process', info, logo, logoAspect)
    if (hasMetrics) {
      y = heading(doc, 'Mesh Metrics', y)
      const bbox = meshMetrics.bbox_mm ? meshMetrics.bbox_mm.map((e) => e.toFixed(1)).join(' × ') + ' mm' : '—'
      const rows = [
        ['Faces', (meshMetrics.faces || 0).toLocaleString()],
        ['Vertices', (meshMetrics.vertices || 0).toLocaleString()],
        ['Watertight', meshMetrics.watertight == null ? '—' : (meshMetrics.watertight ? 'Yes' : 'No')],
        ['Bounding box', bbox],
        ['Volume', meshMetrics.volume_cm3 != null ? `${meshMetrics.volume_cm3.toFixed(1)} cm³` : '—'],
      ]
      y = drawTable(doc, M, y, [['Parameter', 'Value'], ...rows], [60, USABLE - 60], { header: true }) + 5
    }
    if (hasImaging) {
      y = heading(doc, 'Imaging & Quality', y)
      if (imaging.grade) {
        doc.setFont(FONT,'normal'); doc.setFontSize(10); doc.setTextColor(...INK)
        doc.text(`Overall quality: ${imaging.grade} (${imaging.score ?? '?'}/${imaging.max_score ?? 10})`, M, y)
        y += 6
      }
      const iqRows = [['Parameter', 'Value', 'Note']]
      for (const c of imaging.checks || []) iqRows.push([c.label || '', c.value || '', c.note || ''])
      for (const [lab, key] of [['Volume dimensions', 'dimensions'], ['Voxel spacing (mm)', 'spacing_mm'], ['Physical size', 'physical_mm']]) {
        if (imaging[key]) iqRows.push([lab, imaging[key], ''])
      }
      if (iqRows.length > 1) y = drawTable(doc, M, y, iqRows, [50, 45, USABLE - 95], { header: true }) + 5
    }
    if (processSteps) {
      y = heading(doc, 'Processing Steps', y)
      doc.setFont(FONT,'normal'); doc.setFontSize(10); doc.setTextColor(...INK)
      const l = doc.splitTextToSize(esc(processSteps), USABLE); doc.text(l, M, y)
    }
  }

  // stamp watermark + footer on every page
  const pages = doc.getNumberOfPages()
  const ds = dateStr || ''
  for (let p = 1; p <= pages; p++) { doc.setPage(p); stampFrame(doc, p, caseId, ds) }
  return doc
}
